import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import { adoptMe, useCurrentMe } from "../../../app/auth";
import { dropWorkspaceQueries } from "../../../live/sink";

/**
 * Goes into the practice space and back out of it (BR-3). The page forgets what it read in the space it leaves, so
 * nothing from the practice copy shows up in the real discipline, and the other way round. The person stays on the
 * same screen: the getting-started screen is where the tutor is.
 */
export function usePracticeSpace() {
  const me = useCurrentMe();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useApiCommand("set_active_workspace");
  const [problem, setProblem] = useState<string | null>(null);
  const active = me.active;

  const move = async (space: "workspace" | "practice") => {
    if (!active) return;
    setProblem(null);
    try {
      const reply = await mutateAsync({ workspace_code: active.workspace_code, space });
      adoptMe(queryClient, reply.data);
      dropWorkspaceQueries(queryClient);
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return {
    inPractice: active?.space === "practice",
    disciplineName: active?.workspace_name ?? "",
    enter: () => move("practice"),
    leave: () => move("workspace"),
    busy: isPending,
    problem,
  };
}
